import { useState, useEffect } from "react";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../store/adminData/adminData";
import useUserStore, { selectUserDetails } from "../../../store/user/user";
import NavigationSetup from "./NavigationSetup";
import IntroSection from "./IntroSection";
import { ChantInput } from "../../../CommonComponents";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import { db } from "../../../services/firebaseService/connection";
import {
  getWebsiteConfig,
  uploadImage,
} from "../../../services/firebaseService/endPoints/admin/website";
import { Steps } from "antd";
import { doc, getDoc, setDoc } from "@firebase/firestore";
import { postUpdateCDNUtil } from "../../../helperFunctions/util";
import AdminContainer from "../admin-container/AdminContainer";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Setup = () => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];
  const user = useUserStore(selectUserDetails);

  const [social, setSocial] = useState({ facebook: "", insta: "", twitter: "" });
  const [save, setSave] = useState(false);
  const [updatedComponents, setUpdatedComponents] = useState([]);
  const [dataUpdated, setDataUpdated] = useState(false);
  const [navigationStep, setNavigationStep] = useState("process");
  const [introStep, setIntroStep] = useState("wait");
  const [socialStep, setSocialStep] = useState("wait");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saveToFirebase, setSaveToFirebase] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [componentStates, setComponentStates] = useState({});

  const fetchData = () => {
    getWebsiteConfig(currentGroup).then((data) => {
      if (data && Object.keys(data).length > 0) {
        const { social: savedSocial, navigation, intro, ...rest } = data;
        if (savedSocial) setSocial(savedSocial);
        setComponentStates((prev) => ({
          ...prev,
          ...(navigation ? { navigation } : {}),
          ...(intro ? { intro } : {}),
        }));
        setDataUpdated(true);
      } else {
        setSocial({ facebook: "", insta: "", twitter: "" });
        setComponentStates({});
        setDataUpdated(true);
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step transition: when handleNext sets save=true with current/next
  useEffect(() => {
    if (save && current && next) {
      setSave(false);
      const stepSetters = {
        navigationStep: setNavigationStep,
        introStep: setIntroStep,
        socialStep: setSocialStep,
      };
      if (stepSetters[current]) stepSetters[current]("finish");
      if (stepSetters[next]) stepSetters[next]("process");
      setCurrent("");
      setNext("");
      setDataUpdated(true);
    }
  }, [save, current, next]);

  // Clear dataUpdated flag
  useEffect(() => {
    if (dataUpdated) {
      setDataUpdated(false);
    }
  }, [dataUpdated]);

  // Save to Firebase when updatedComponents has 2 entries and saveToFirebase is true
  useEffect(() => {
    if (updatedComponents.length === 2 && saveToFirebase) {
      const doSave = async () => {
        const savedUpdatedComponents = [...updatedComponents];
        const currentComponentStates = { ...componentStates };

        let path = `${adminData.data.groupName}/website/`;
        let imageUploadPromises = [];

        for (let comp of savedUpdatedComponents) {
          let imageFileKeys = currentComponentStates[comp]?.imageFileKeys;
          if (imageFileKeys) {
            for (let key of imageFileKeys) {
              if (Array.isArray(currentComponentStates[comp][key])) {
                let images = currentComponentStates[comp][key];
                let index = 0;
                for (let image of images) {
                  if (image && typeof image === "object" && !image.url) {
                    imageUploadPromises.push(
                      uploadImage(
                        image,
                        `${path}${comp}/${key}/${index + 1}`,
                        index
                      ).then(async ({ downloadUrl, index: imgIndex }) => {
                        currentComponentStates[comp][key][imgIndex] = await downloadUrl;
                      })
                    );
                    await delay(1000);
                  }
                  index++;
                }
              } else if (key.includes(".")) {
                let keyParts = key.split(".");
                let file = currentComponentStates[comp][keyParts[0]][keyParts[1]];
                if (file && typeof file === "object" && !file.url) {
                  imageUploadPromises.push(
                    uploadImage(file, `${path}${comp}/${keyParts[0]}`)
                      .then(({ downloadUrl }) => downloadUrl)
                      .then((downloadUrl) => {
                        currentComponentStates[comp][keyParts[0]][keyParts[1]] = downloadUrl;
                      })
                  );
                  await delay(1000);
                }
              } else {
                let image = currentComponentStates[comp][key];
                if (image && typeof image === "object" && !image.url) {
                  imageUploadPromises.push(
                    uploadImage(image, `${path}${comp}/${key}`)
                      .then(({ downloadUrl }) => downloadUrl)
                      .then((downloadUrl) => {
                        currentComponentStates[comp][key] = downloadUrl;
                      })
                  );
                  await delay(1000);
                }
              }
            }
          }
        }

        for (let comp of savedUpdatedComponents) {
          delete currentComponentStates[comp]?.imageFileKeys;
        }

        await Promise.all(imageUploadPromises);

        let docRef = doc(db, "website", adminData.data.id);
        let groupData = adminData.data;
        let websiteUpdate = {
          clubName: groupData.clubName || "",
          leagueName: groupData.leagueName || "",
          groupName: groupData.groupName || "",
          country: groupData.country || "",
        };
        if (groupData.clubId) websiteUpdate.clubId = groupData.clubId;
        if (groupData.leagueId) websiteUpdate.leagueId = groupData.leagueId;

        getDoc(docRef).then((docSnap) => {
          let websiteConfig = docSnap.data() || {};
          if (!(websiteConfig && typeof websiteConfig === "object")) websiteConfig = {};
          websiteConfig.intro = currentComponentStates.intro || {};
          websiteConfig.navigation = currentComponentStates.navigation || {};
          websiteConfig.social = social || {};
          setDoc(
            docRef,
            { ...websiteConfig, ...websiteUpdate, configuration: groupData.configuration || {} },
            { merge: true }
          ).then(() => {
            postUpdateCDNUtil(currentGroup);
            setUpdatedComponents([]);
            setSave(false);
            setSaveToFirebase(false);
            setNavigationStep("process");
            setSocialStep("wait");
            setIsSaving(false);
            fetchData();
          });
        });
      };

      doSave();
    }
  }, [updatedComponents, saveToFirebase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePublish = () => {
    setSaveToFirebase(true);
    setIsSaving(true);
  };

  const handleInputChange = (e) => {
    setSocial((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveComponentState = (state) => {
    const key = Object.keys(state)[0];
    setComponentStates((prev) => ({ ...prev, ...state }));
    setUpdatedComponents((prev) => [...prev, key]);
  };

  const handleNext = (currentStep, nextStep) => {
    setCurrent(currentStep);
    setNext(nextStep);
    setSave(true);
  };

  let primaryColor = "";
  if (adminData?.data?.configuration?.primaryColor) {
    const { r, g, b } = adminData.data.configuration.primaryColor;
    primaryColor = `rgb(${r}, ${g}, ${b})`;
  }

  let websiteUrl = `https://group.chant.fan/${(adminData?.data?.groupName || "").split(" ").join("").toLowerCase()}`;

  const currentData = {
    adminData,
    user,
    currentGroup,
  };
  currentData.adminData = { ...adminData, data: { ...adminData?.data, websiteConfig: { social, ...componentStates } } };

  return (
    <AdminContainer>
      <Steps>
        <Steps.Step status={navigationStep} title="Navigation" />
        <Steps.Step status={introStep} title="Header, Intro & Section Breaks" />
        <Steps.Step status={socialStep} title="Social" />
      </Steps>
      {navigationStep === "process" && (
        <>
          <div className="mt-3">
            <p style={{ fontSize: "20", fontWeight: "bold", marginBottom: 0 }}>
              Website Setup
            </p>

            <p>
              Chant offers a hosted, single-page website for your supporters
              group with news, events, tickets, gallery, store, member
              registration, leader bios and more. Use of the website solution
              is $10/month (paid annually).
            </p>

            <p>
              Content like Events, News and Gallery are integrated across the
              app and website (read: low maintenance!). And you can &apos;Pin
              to website&apos; any chat stream post from the app.
            </p>

            <p>To launch, simply complete the form and click &apos;Publish&apos;. </p>

            <p>
              Your URL (when published): <a href={websiteUrl}>{websiteUrl}</a>
            </p>
          </div>

          <NavigationSetup
            {...currentData}
            updated={dataUpdated}
            onSave={handleSaveComponentState}
            save={save}
            isUpdated={updatedComponents.includes("navigation")}
          />

          <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
            <ChantButton
              onClick={(e) => {
                e.preventDefault();
                handleNext("navigationStep", "introStep");
              }}
            >
              Next
            </ChantButton>
          </div>
        </>
      )}

      {introStep === "process" && (
        <>
          <IntroSection
            {...currentData}
            updated={dataUpdated}
            onSave={handleSaveComponentState}
            save={save}
            isUpdated={updatedComponents.includes("intro")}
          />

          <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
            <ChantButton
              onClick={(e) => {
                e.preventDefault();
                handleNext("introStep", "socialStep");
              }}
            >
              Next
            </ChantButton>
          </div>
        </>
      )}

      {socialStep === "process" && (
        <div className="mt-4">
          <p style={{ fontSize: 20, fontWeight: "bold" }}>Social</p>
          <div className="chant-form-body">
            <ChantInput
              label="Facebook"
              value={social.facebook}
              onChange={handleInputChange}
              name="facebook"
              placeholder="(optional) Enter Facebook Link"
            />
            <ChantInput
              label="Instagram"
              value={social.insta}
              onChange={handleInputChange}
              name="insta"
              placeholder="(optional) Enter Instagram Link"
            />
            <ChantInput
              label="Twitter"
              value={social.twitter}
              onChange={handleInputChange}
              name="twitter"
              placeholder="(optional) Enter Twitter Link"
            />
            <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
              <ChantButton loading={isSaving} onClick={handlePublish}>
                Save
              </ChantButton>
            </div>
          </div>
        </div>
      )}
    </AdminContainer>
  );
};

export default Setup;
